import { Space } from "../../../types/spaces.types";
import { baseApi } from "../app-main/store/baseApi";

/** One space's "reviews last viewed" timestamp for the authenticated owner. */
export type OwnerSpaceReviewsLastViewed = {
  /** ISO timestamp, or `null` when the host has never viewed the space's reviews. */
  reviews_last_viewed_at: string | null;
  space_id: string;
};

export type OwnerReviewsLastViewedResponse = {
  spaces: OwnerSpaceReviewsLastViewed[];
};

export type MarkSpaceReviewsViewedArgs = {
  spaceId: string;
};

/** The space document returned after marking its reviews as viewed. */
export type MarkSpaceReviewsViewedResponse = {
  reviews_last_viewed_at: string;
};

export type OwnerSpaceImage = {
  external_id?: string;
  urlSmall?: string;
};

export type OwnerSpace = {
  _id: string;
  title?: string;
  images?: OwnerSpaceImage[];
};

export type GetSpaceArgs = {
  spaceId: string;
};

/**
 * The subset of a space's fields edited via `PUT /v1/spaces/:id`
 */
export type UpdateSpacePayload = Partial<
  Pick<
    Space,
    | "title"
    | "venue_name"
    | "description"
    | "sqft"
    | "sqm"
    | "rules"
    | "min_age"
    | "wifi_instructions"
    | "getting_there"
  >
>;

export type UpdateSpaceArgs = {
  spaceId: string;
  payload: UpdateSpacePayload;
};

export type OwnerSpacesResponse = {
  spaces: OwnerSpace[];
};

export type GetOwnerSpacesArgs = {
  ssoId: string;
};

export const spacesApi = baseApi.injectEndpoints({
  endpoints: (builder) => ({
    // Auth is taken from the token (no SSO id in the path); returns the
    // `reviews_last_viewed_at` timestamp per space the host owns.
    getOwnerReviewsLastViewed: builder.query<
      OwnerReviewsLastViewedResponse,
      void
    >({
      query: () => ({
        url: "/v2/spaces-v2/spaces/owner/reviews-last-viewed",
      }),
    }),

    // The boolean `true` is a trigger flag; the server sets the actual
    // timestamp (clients cannot supply one directly).
    markSpaceReviewsViewed: builder.mutation<
      MarkSpaceReviewsViewedResponse,
      MarkSpaceReviewsViewedArgs
    >({
      query: ({ spaceId }) => ({
        url: `/v2/spaces-v2/spaces/${spaceId}`,
        method: "PUT",
        body: { reviews_last_viewed_at: true },
      }),
    }),

    /** Host-owned spaces with titles and images for calendar-sync rows. */
    getOwnerSpaces: builder.query<OwnerSpacesResponse, GetOwnerSpacesArgs>({
      query: ({ ssoId }) => ({
        url: `/v1/spaces/owner/${ssoId}`,
        params: {
          full: true,
          no_deactivated: true,
          limit: 400,
        },
      }),
    }),

    /** A single space by id, e.g. for reading its `country` (currency lookup). */
    getSpace: builder.query<Space, GetSpaceArgs>({
      query: ({ spaceId }) => ({
        url: `/v1/spaces/${spaceId}`,
      }),
    }),

    /**
     * Generic partial update for a space
     */
    updateSpace: builder.mutation<Space, UpdateSpaceArgs>({
      query: ({ spaceId, payload }) => ({
        url: `/v1/spaces/${spaceId}`,
        method: "PUT",
        body: payload,
      }),
    }),
  }),
  overrideExisting: false,
});

export const {
  useGetOwnerReviewsLastViewedQuery,
  useMarkSpaceReviewsViewedMutation,
  useGetOwnerSpacesQuery,
  useGetSpaceQuery,
  useUpdateSpaceMutation,
} = spacesApi;
